import { Flex, Modal, notification, Spin } from "antd";
import {
  StyledBigText,
  StyledBox,
  StyledLightText,
  StyledPercentageText,
  StyledTitleText,
} from "./style";
import { Chart as ChartJS, ArcElement, Tooltip, Legend } from "chart.js";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useAppDispatch, useAppSelector } from "../../store/config";
import {
  getDashboardStatsStart,
  resetDashboard,
} from "../../store/features/dashboard/stats/stats-slice";
import { ActiveUsers } from "./ActiveUsers";
import { Revenues } from "./Revenues";
import { useParams } from "react-router-dom";
import { PredictedIncome } from "./PredictedIncome";
import { FullWidthFlex } from "../../components/common/styles";
import { formatNumber } from "../../utils/utils";
import { ArrowDownOutlined, ArrowUpOutlined } from "@ant-design/icons";

ChartJS.register(ArcElement, Tooltip, Legend);

export const Dashboard = (props: { isAdmin?: boolean }) => {
  const { isAdmin } = props;
  const { t } = useTranslation();
  const dispatch = useAppDispatch();
  const { id: trainerId } = useParams<{ id: string }>();
  const [api, contextHolder] = notification.useNotification();

  const [isMobile, setIsMobile] = useState<boolean>(true);
  const [incomeHistoryModalOpen, setIncomeHistoryModalOpen] =
    useState<boolean>(false);

  const {
    traineesCount,
    currentMonthRevenue,
    traineesCount30DaysAgo,
    totalRevenueSoFar,
    error,
    loading,
  } = useAppSelector((state) => state.dashboardStats);
  const { user } = useAppSelector((state) => state.auth);

  useEffect(() => {
    dispatch(getDashboardStatsStart({ trainerId }));
    return () => {
      dispatch(resetDashboard());
    };
  }, []);

  useEffect(() => {
    const userAgent = window.navigator.userAgent.toLowerCase();
    const isMobileDevice =
      /android|iphone|ipad|ipod|blackberry|iemobile|opera mini/.test(userAgent);
    setIsMobile(isMobileDevice);

    if (isMobileDevice) {
      Modal.warning({
        title: t("Error"),
        content: t(
          "System not supported on mobile devices. Please use a computer."
        ),
        closable: false,
        maskClosable: false,
        okButtonProps: { style: { display: "none" } },
        style: {
          direction: "rtl",
        },
      });
    }
  }, []);

  useEffect(() => {
    if (error) {
      openNotification();
    }
  }, [error]);

  const openNotification = () => {
    api["error"]({
      message: t("Something went wrong, please try again"),
      placement: "bottomLeft",
    });
  };

  const getGreeting = () => {
    const hour = new Date().getHours();
    if (hour >= 5 && hour < 12) return t("Good Morning");
    if (hour >= 12 && hour < 18) return t("Good Afternoon");
    if (hour >= 18 && hour < 21) return t("Good Evening");
    return t("Good Night");
  };

  const calculateGrowthPercentage = (
    currentTotal: number,
    previousTotal: number
  ) => {
    if (previousTotal === 0) {
      return {
        isPositive: false,
        isNegative: false,
        growthPercentage: "0%",
      };
    }
    const growth = ((currentTotal - previousTotal) / previousTotal) * 100;
    const formattedGrowth = formatNumber(Math.abs(growth), true);
    const isPositive = growth > 0;
    const isNegative = growth < 0;

    return {
      isPositive,
      isNegative,
      growthPercentage: `${formattedGrowth}%${
        isPositive ? "+" : isNegative ? "-" : ""
      }`,
    };
  };

  const renderGrowthIndicator = () => {
    const growth = calculateGrowthPercentage(
      traineesCount?.total || 0,
      traineesCount30DaysAgo?.traineesTotal || 0
    );

    const { isPositive, isNegative, growthPercentage } = growth;

    return (
      <>
        {isPositive ? (
          <ArrowUpOutlined style={{ fontSize: "12px", color: "#22C55E" }} />
        ) : isNegative ? (
          <ArrowDownOutlined style={{ fontSize: "12px", color: "#EF4444" }} />
        ) : null}
        <StyledPercentageText
          color={isPositive ? "green" : isNegative ? "red" : "gray"}
        >
          {growthPercentage}
        </StyledPercentageText>
      </>
    );
  };

  if (!isMobile)
    return (
      <Spin size="large" spinning={loading && !incomeHistoryModalOpen}>
        <Flex
          style={{ padding: isAdmin ? "20px" : "20px 40px" }}
          gap={20}
          vertical
        >
          {contextHolder}
          {!isAdmin && (
            <StyledTitleText>
              {getGreeting()},&nbsp;
              <b>{user?.name}</b>
            </StyledTitleText>
          )}

          <FullWidthFlex gap={20}>
            <StyledBox>
              <StyledLightText>{t("Total Trainees Count")}</StyledLightText>
              <Flex justify="space-between" align="end">
                <StyledBigText>{traineesCount?.total}</StyledBigText>
                <Flex gap={5}>{renderGrowthIndicator()}</Flex>
              </Flex>
            </StyledBox>
            <StyledBox>
              <StyledLightText>{t("User Age Range")}</StyledLightText>
              <StyledBigText>
                {traineesCount?.minAge}-{traineesCount?.maxAge}
              </StyledBigText>
            </StyledBox>
            <StyledBox>
              <StyledLightText>{t("Total Female Trainees")}</StyledLightText>
              <Flex justify="space-between" align="end">
                <StyledBigText>{traineesCount?.female}</StyledBigText>
                <StyledPercentageText color="gray">
                  {Math.round(
                    ((traineesCount?.female || 0) /
                      (traineesCount?.total || 1)) *
                      100
                  )}
                  %
                </StyledPercentageText>
              </Flex>
            </StyledBox>
            <StyledBox>
              <StyledLightText>{t("Total Male Trainees")}</StyledLightText>
              <Flex justify="space-between" align="end">
                <StyledBigText>{traineesCount?.male}</StyledBigText>
                <StyledPercentageText color="gray">
                  {Math.round(
                    ((traineesCount?.male || 0) / (traineesCount?.total || 1)) *
                      100
                  )}
                  %
                </StyledPercentageText>
              </Flex>
            </StyledBox>
          </FullWidthFlex>

          <FullWidthFlex gap={20}>
            <ActiveUsers />

            <Flex style={{ width: "60%" }} vertical gap={20}>
              <PredictedIncome
                revenue={currentMonthRevenue?.revenue || 0}
                projectedRevenue={currentMonthRevenue?.projectedRevenue || 0}
              />
              <Revenues
                incomeHistoryModalOpen={incomeHistoryModalOpen}
                setIncomeHistoryModalOpen={setIncomeHistoryModalOpen}
                totalRevenue={totalRevenueSoFar || 0}
              />
            </Flex>
          </FullWidthFlex>
        </Flex>
      </Spin>
    );

  return null;
};
